import React, {
  useEffect, useMemo, useRef, useState,
} from 'lib/react';
import {
  EVCircularProgress, EVGrid, EVPaper, EVTypography,
} from 'lib/components';
import { useParams } from 'lib/router';
import TitleHeader from 'commonComponents/titleHeader';
import Banner from 'commonComponents/banner';
import useMediaQuery from 'hooks/useMediaQuery';
import getSitemapImageService from 'services/getSitemapImage.service';
import getReportDetailsService from 'services/getReportDetails.service';
import {
  INFO, KEY, INITIALIZATION_VECTOR,
} from 'constants/AppConstants';
import { DASHBOARD_ROUTE } from 'constants/RouteConstants';
import { LOAD_SUBSCRIPTION_DASHBOARD } from 'constants/ActionTypes';
import { useDispatch, useSelector } from 'lib/redux';
import { isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import { isAuthorized } from 'utils/auth.utils';
import { decodeAndDecrypt } from 'utils/app.utils';
import { logError } from 'utils/logger.utils';
import SITEMAP_SUBMITTED_PLACEHOLDERS from './sitemapSubmittedPlaceholders';
import { readSitemapSubmittedPayload } from './sitemapSubmittedStorage';
import {
  formatSitemapEstimatedCost,
  formatSitemapOrderPlaced,
  formatSitemapReportAddress,
} from './sitemapStatus.utils';
import { resolveSitemapSubscriptionUsage } from './sitemapSubscriptionUsage.utils';
import SitemapSubmittedStyles from './SitemapSubmittedStyles';
import SitemapSubmittedSummary from './components/SitemapSubmittedSummary.component';
import SitemapSubmittedDetails from './components/SitemapSubmittedDetails.component';
import {
  openServiceRequestForm,
  SITEMAP_SERVICE_REQUEST_URL,
} from './components/SitemapApprovalErrorDialog.component';

function SitemapSubmitted() {
  const classes = SitemapSubmittedStyles();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(640);
  const { encryptedReportId: pathEncryptedReportId } = useParams();
  const payload = readSitemapSubmittedPayload() || {};
  const {
    encryptedReportId: payloadEncryptedReportId,
    address: payloadAddress,
    orderPlacedDate: payloadOrderPlacedDate,
    productType: payloadProductType,
    splitResponse,
    selectedBuildingIds: payloadSelectedBuildingIds,
  } = payload;

  const encryptedReportId = pathEncryptedReportId || payloadEncryptedReportId;
  const hasFreshSubmit = Boolean(
    splitResponse?.reportBuildingMappings?.length
    && (!pathEncryptedReportId || payloadEncryptedReportId === pathEncryptedReportId),
  );

  const placeholders = SITEMAP_SUBMITTED_PLACEHOLDERS;
  const authorized = isAuthorized();
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const primarySubscriptionProductsWithUsage = useSelector(
    ({ order }) => order.primarySubscriptionProductsWithUsage ?? [],
  );
  const subscriptions = useSelector(
    ({ userData }) => userData?.subscriptionDashboard?.subscriptions ?? [],
  );
  const subscriptionDashboardLoading = useSelector(
    ({ userData }) => Boolean(userData?.loading?.subscriptionDashboard),
  );

  // Load subscription dashboard only for authorized V2 users (never for anonymous email visitors).
  useEffect(() => {
    if (!authorized || !isSubscriptionDashboardV2Enabled) return;
    dispatch({ type: LOAD_SUBSCRIPTION_DASHBOARD });
  }, [dispatch, authorized, isSubscriptionDashboardV2Enabled]);

  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [imageLoading, setImageLoading] = useState(Boolean(encryptedReportId));
  const [confirmedReportId, setConfirmedReportId] = useState(
    hasFreshSubmit ? (payload.reportId || null) : null,
  );
  const [reportDetails, setReportDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(!hasFreshSubmit && Boolean(encryptedReportId));
  const imageUrlRef = useRef(null);

  useEffect(() => {
    if (hasFreshSubmit || !encryptedReportId) {
      setDetailsLoading(false);
      return undefined;
    }

    let active = true;

    const loadAlreadyConfirmedDetails = async () => {
      setDetailsLoading(true);
      try {
        const reportId = await decodeAndDecrypt(encryptedReportId, KEY, INITIALIZATION_VECTOR);
        if (!active) return;
        setConfirmedReportId(reportId);

        const detailsResult = await getReportDetailsService(reportId);
        if (active && detailsResult.ok) {
          setReportDetails(detailsResult.json);
        }
      } catch (error) {
        logError('SitemapSubmitted decrypt/details failed', { error: error.message });
      } finally {
        if (active) setDetailsLoading(false);
      }
    };

    loadAlreadyConfirmedDetails();
    return () => { active = false; };
  }, [hasFreshSubmit, encryptedReportId]);

  useEffect(() => {
    let active = true;

    const loadImage = async () => {
      if (!encryptedReportId) {
        setImageLoading(false);
        setImageError('Sitemap image unavailable');
        return;
      }

      setImageLoading(true);
      setImageError(null);

      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
        imageUrlRef.current = null;
        setImageUrl(null);
      }

      const result = await getSitemapImageService(encryptedReportId);
      if (!active) {
        if (result.ok && result.imageUrl) {
          URL.revokeObjectURL(result.imageUrl);
        }
        return;
      }

      if (result.ok && result.imageUrl) {
        imageUrlRef.current = result.imageUrl;
        setImageUrl(result.imageUrl);
      } else {
        setImageError('Unable to load sitemap image');
      }
      setImageLoading(false);
    };

    loadImage();

    return () => {
      active = false;
      if (imageUrlRef.current) {
        URL.revokeObjectURL(imageUrlRef.current);
        imageUrlRef.current = null;
      }
    };
  }, [encryptedReportId]);

  const address = payloadAddress
    || formatSitemapReportAddress(reportDetails?.reportAddress)
    || '—';
  const orderPlacedRaw = payloadOrderPlacedDate || reportDetails?.orderPlacedDate;
  const orderPlacedDisplay = formatSitemapOrderPlaced(orderPlacedRaw, { includeTime: false });

  const structureCount = payloadSelectedBuildingIds?.length
    || splitResponse?.reportBuildingMappings?.length
    || 0;
  const productType = payloadProductType
    || splitResponse?.reportBuildingMappings?.[0]?.productType
    || reportDetails?.reportType
    || '';

  const subscriptionUsage = useMemo(() => {
    // Unauthorized / email visitors: never call or derive subscription Usage.
    if (!authorized || !isSubscriptionDashboardV2Enabled) return null;
    return resolveSitemapSubscriptionUsage({
      productType,
      structureCount,
      isSubscriptionDashboardV2Enabled,
      primarySubscriptionProductsWithUsage,
      subscriptions,
    });
  }, [
    authorized,
    isSubscriptionDashboardV2Enabled,
    productType,
    structureCount,
    primarySubscriptionProductsWithUsage,
    subscriptions,
  ]);

  const showUsage = hasFreshSubmit && Boolean(subscriptionUsage?.usage);
  const consumedUnits = subscriptionUsage?.unitsOrdered || structureCount;
  const estimatedCostDisplay = subscriptionUsage
    ? formatSitemapEstimatedCost(subscriptionUsage.estimatedCost)
    : formatSitemapEstimatedCost(splitResponse?.estimatedCost);

  const detailRows = useMemo(() => {
    const mappings = splitResponse?.reportBuildingMappings || [];
    return mappings.map((mapping) => ({
      reportId: mapping.reportId,
      buildingId: mapping.buildingId,
      productType: mapping.productType || payloadProductType || placeholders.productType,
      delivery: mapping.productDelivery || placeholders.delivery,
      address,
    }));
  }, [
    splitResponse,
    payloadProductType,
    placeholders.productType,
    placeholders.delivery,
    address,
  ]);

  const webFormLink = (
    <a
      href={SITEMAP_SERVICE_REQUEST_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={classes.alertLink}
      onClick={openServiceRequestForm}
    >
      web form request
    </a>
  );

  const alreadyConfirmedMessage = confirmedReportId
    ? `${confirmedReportId} report has already been confirmed for structures of sitemap`
    : 'This report has already been confirmed for structures of sitemap';

  const waitingOnSubscription = hasFreshSubmit
    && authorized
    && isSubscriptionDashboardV2Enabled
    && subscriptionDashboardLoading;
  const summaryLoading = imageLoading
    || (!hasFreshSubmit && detailsLoading)
    || waitingOnSubscription;

  return (
    <EVGrid className={classes.pageShell}>
      <TitleHeader
        title="Order Submitted"
        breadcrumbLinks={[{ title: 'Dashboard', link: DASHBOARD_ROUTE }]}
      />

      {hasFreshSubmit ? (
        <Banner
          severity={INFO}
          className={classes.infoBanner}
          message={(
            <>
              Our measurement technicians have received your selections and the report creation
              process will continue. You will receive an email for your records in the meantime. If
              a mistake was made, please submit this
              {' '}
              {webFormLink}
              .
            </>
          )}
        />
      ) : (
        <Banner
          severity={INFO}
          className={classes.infoBanner}
          message={alreadyConfirmedMessage}
        />
      )}

      <EVPaper square={isMobile} elevation={0} className={classes.paper}>
        {hasFreshSubmit && (
          <>
            <EVTypography className={classes.successTitle}>
              Order change submitted successfully
            </EVTypography>
            <EVTypography className={classes.reasonText}>
              {'Reason for change: '}
              {placeholders.reasonForChange}
            </EVTypography>
          </>
        )}

        {summaryLoading ? (
          <EVGrid className={classes.loaderContainer}>
            <EVCircularProgress size={36} />
          </EVGrid>
        ) : (
          <SitemapSubmittedSummary
            classes={classes}
            orderPlacedDisplay={orderPlacedDisplay}
            address={address}
            estimatedCost={estimatedCostDisplay}
            consumedUnits={consumedUnits}
            usage={subscriptionUsage?.usage || null}
            imageUrl={imageUrl}
            imageError={imageError}
            showEstimatedCost={hasFreshSubmit}
            showUsage={showUsage}
          />
        )}

        {hasFreshSubmit && (
          <>
            <EVTypography className={classes.detailsHeading}>Order Change Details</EVTypography>
            <SitemapSubmittedDetails classes={classes} rows={detailRows} />
          </>
        )}
      </EVPaper>
    </EVGrid>
  );
}

export default SitemapSubmitted;
